/**
 * ── THE APPLIED-FILTER CHIP ROW ─────────────────────────────────────────────────────
 *
 * One chip per APPLIED DIMENSION, immediately below the action line.
 *
 * ## What it fixes
 *
 * `Filter (3)` says HOW MANY dimensions are narrowing the page and never WHICH. That is
 * state a reader cannot check without opening a drawer, on a page whose every number is
 * a function of exactly that state — and it is the same defect that decided the
 * inline-vs-drawer question on the Business figures card. The count is not wrong, it is
 * unreadable: three filters and three *different* filters look identical.
 *
 * ## FOUR constraints, each of which is the difference between this helping and this
 * becoming clutter
 *
 * 1. **ONE CHIP PER FIELD, NEVER PER VALUE.** Eight selected countries render as
 *    `Location: France, Italy +6`, not eight chips. Per-value chips turn the row itself
 *    into the thing that needs scanning, which is the problem it exists to solve — and
 *    a multi-select is precisely where the count badge was least informative, so it is
 *    the case this must not regress. Clicking the chip reopens THAT field in the drawer,
 *    where the individual values already have their own controls.
 *
 * 2. **ONLY OPTIONAL NARROWING GETS A CHIP — WITH ONE DELIBERATE EXCEPTION.** Not either
 *    panel's Scans/Certificates dimension: that is ALWAYS set — a panel always counts
 *    something — so its chip would be permanent furniture, and a row that is always
 *    populated is one people learn to stop reading.
 *
 *    The date range USED to be excluded on the same reasoning (a range always has a
 *    value). It no longer is. `AnalyticsFiltersHeader`'s toolbar date control now DROPS
 *    OFF SCREEN ENTIRELY on a narrow viewport rather than wrapping, and the date range is
 *    the one control that scopes every other number on the page — losing it with nothing
 *    else naming the active window is the exact "affordance offered where the data cannot
 *    honour it" defect this epic has hit before. So the date chip is unconditional, always
 *    first, and is the one chip this row can never be without.
 *
 * 3. **ZERO HEIGHT ONLY WHEN NOTHING IS APPLIED AND THE DATE CHIP RESOLVES.** The date
 *    chip is always shown, so the row is no longer zero-height in the general case — that
 *    guarantee now belongs only to constraint 2's original targets. What survives from
 *    the original design: the row still never grows a SECOND permanent chip, and the
 *    optional-narrowing chips still appear and disappear on their own, unannounced by any
 *    reserved space of their own.
 *
 * 4. **NOT IN THE PINNED BAND.** This renders BELOW the sticky control block, so it
 *    scrolls. The band is already 143px (225px with the shell header — over a quarter of
 *    an 800px viewport), and a variable-height region inside a pinned one is how a band
 *    creeps: it would grow by a row every time a filter wrapped. The `Filter` badge pins
 *    and keeps carrying the count; the detail scrolls.
 *
 * ## Where the labels come from, and the asymmetry in how they are read
 *
 * Four of the five fields store IDs, so a chip needs the entity lists to say anything
 * readable. Those lists are already fetched by `AnalyticsFiltersHeader`, one row above,
 * and this component deliberately does NOT fetch them again — but the two mechanisms
 * differ and the difference is load-bearing:
 *
 *   products      `useProducts` is react-query, keyed on its params, so calling it here
 *                 with the same params SUBSCRIBES to the header's cache entry and issues
 *                 no second request.
 *   owners        `useOwners` / `useCertificates` are `useQuerySelect` — each call site
 *   certificates  owns its own `prevParamsRef` and DISPATCHES, so calling them here
 *                 would fire a second GET for a list already in the store. They are read
 *                 straight off the redux slot instead.
 *
 * ⚠️ That makes this component dependent on the header having mounted, which on this
 * page it always has (they are siblings rendered by `AnalyticsPage`, and a filter cannot
 * be applied without the drawer that sets it). If a chip ever renders without labels,
 * that coupling is the first thing to check. It degrades honestly rather than lying —
 * see `describeValues`.
 */
import React, { useMemo } from 'react'

import dayjs, { type Dayjs } from 'dayjs'
import type { DateRange } from '@mui/x-date-pickers-pro'

// `Typography` left with `ChipLabel` when it moved to `./analyticsChipLabel` (VRG-512) —
// it was this file's only user of it.
import { Box, Stack, Tooltip } from '@mui/material'
import { XIcon } from 'lucide-react'

import { useDispatch, useSelector } from 'store/hooks'
import { setAnalyticsFilters } from 'store/analytics/actions'
import { BOOTSTRAP_DATE_RANGE, billingPeriodDateRange } from 'store/analytics/reducer'
import { useProducts } from 'hooks/api'
import { TagChip } from 'components/input/TagChip'
import { focusScansMap } from 'pages/main/AnalyticsPage/components/ScansByLocation/ScansByLocation'
import {
  DATE_RANGE_FIELD_FORMAT,
  makeDateRangeShortcutItems,
  matchDateRangeShortcut,
} from 'components/input/filters/dateRangeChrome'
import {
  dateChipInverseSx,
  scanStatusChipInverseSx,
  statusChipInverseSx,
  toolbarContainerSx,
} from './AnalyticsFiltersHeader'
import { ChipLabel, CHIP_SX } from './analyticsChipLabel'

import {
  CERT_STATUS_OPTIONS,
  FILTER_FIELDS,
  SCAN_STATUS_OPTIONS,
  collapseStatuses,
  type AnalyticsFilterField,
} from './analyticsFilterFields'

/**
 * How many values a chip names before it collapses the rest into `+N`.
 *
 * TWO. One reads as "and there might be more" without saying so; three pushes a chip
 * past the width at which the row still scans as a list of DIMENSIONS rather than a list
 * of values — which is constraint 1 above, reintroduced by the back door. The full set
 * is one hover away (see the tooltip) and one click away (the drawer).
 */
const MAX_NAMED_VALUES = 2

/**
 * The chip's leading word, matching what the drawer calls the same field.
 *
 * "Certificate Status" and not "Status" is the drawer's own wording, and for its own
 * reason: this page counts two things, and a scan carries its own status vocabulary
 * (invalid / tampered / verified) drawn beside the map. A chip saying `Status: Active`
 * would name neither. Two labels for one field is the drift §44 is about, so these are
 * copied from the drawer deliberately and must move together.
 */
const FIELD_LABELS: Record<AnalyticsFilterField, string> = {
  [FILTER_FIELDS.status]: 'Certificate Status',
  /**
   * "Scan Status", never a bare "Status" — it shares this row with the certificate axis
   * above, and the two describe different entities (§44). Matching the drawer field's own
   * header exactly, for the same reason the certificate chip matches its own.
   */
  [FILTER_FIELDS.scanStatus]: 'Scan Status',
  [FILTER_FIELDS.owner]: 'Owner',
  [FILTER_FIELDS.product]: 'Product',
  [FILTER_FIELDS.certificate]: 'Certificate',
  [FILTER_FIELDS.location]: 'Location',
  /**
   * "Location", NOT "Area" — deliberately the same noun as the country filter above.
   *
   * Both narrow the page by WHERE, and a user does not hold "area" and "location" as
   * two concepts; they hold one. Two nouns for one idea is the drift §44 warns about,
   * so they share it and the VALUE distinguishes them: `Location: Italy` is a country,
   * `Location: drawn` is a shape on the map.
   *
   * ⚠️ This does mean two chips can carry the same field label at once. That is correct
   * — they are two genuinely different location constraints, ANDed together, and a
   * reader needs to see both. It is not a duplicate to be collapsed.
   */
  [FILTER_FIELDS.area]: 'Location',
}

/**
 * Render order for the DRAWER-EDITABLE chips — the drawer's own field order, so the two
 * surfaces agree. `area` is deliberately absent: it has no drawer column (see
 * `FILTER_FIELDS`) and is rendered separately, after this list, because its editing
 * surface — the map's draw control — is not something this ordering says anything about.
 */
const FIELD_ORDER: AnalyticsFilterField[] = [
  FILTER_FIELDS.status,
  FILTER_FIELDS.scanStatus,
  FILTER_FIELDS.owner,
  FILTER_FIELDS.product,
  FILTER_FIELDS.certificate,
  FILTER_FIELDS.location,
]

interface AnalyticsAppliedFiltersProps {
  /**
   * Reopen the drawer at this field. The page owns the request token; see
   * `FilterOpenRequest`.
   */
  onOpenFilter: (field: AnalyticsFilterField) => void
}

/**
 * `{ named, total }` for one field: the labels a chip can actually show, and how many
 * values it stands for.
 *
 * ## Why `total` is counted separately from `named.length`
 *
 * The three entity lists are capped at 100 rows, so a selected id can be absent from
 * them and have no label at all. Deriving the `+N` from the labels we happen to hold
 * would then UNDERSTATE the filter — `Owner: Ana` for a three-owner selection is a
 * false statement about what the page is showing, and it is false in the direction a
 * reader cannot detect. `total` is always the real selection size, so `+N` is honest
 * even when a name is missing, and a field that resolves nothing degrades to
 * `Owner: 3 selected` rather than to a chip that lies.
 */
interface DescribedValues {
  named: string[]
  total: number
}

const describeValues = (values: string[], labelFor: (value: string) => string | undefined): DescribedValues => ({
  named: values.map(labelFor).filter((label): label is string => !!label),
  total: values.length,
})

/**
 * The chip's inner `field: value` markup, shared by every chip — including the area chip
 * below, which is not built from `DescribedValues` and would otherwise be the one chip
 * hand-rolling this instead of reusing it (§28/§36).
 */
/*
  `ChipLabel` and `CHIP_SX` live in `./analyticsChipLabel` (VRG-512) and are imported above
  rather than re-declared here.

  ⚠️ THE SECOND CALLER THAT TRIGGERED THE EXTRACTION IS GONE — the "View by" control's
  chip-shaped fallback, deleted when the bucket became per-card. They stay extracted anyway:
  moving eight lines back into this file to chase a one-caller rule would be churn with a
  merge conflict attached, and the next chip-shaped thing would extract them again. See that
  module's header, which records the same correction.
*/

export const AnalyticsAppliedFilters = ({ onOpenFilter }: AnalyticsAppliedFiltersProps) => {
  const dispatch = useDispatch()
  const filters = useSelector(state => state.analytics.filters)

  // Redux slots, READ ONLY — see the header note above for why these are not the hooks.
  const owners = useSelector(state => state.owners.owners)
  const certificates = useSelector(state => state.certificates.certificates)
  // react-query, same key as the header's call, so this subscribes rather than refetches.
  const { data: productsData } = useProducts({ limit: 100 })

  /**
   * ── THE DATE CHIP'S TEXT — REUSED, NOT RE-DERIVED (VERA-260) ──────────────────────
   *
   * `matchDateRangeShortcut` / `makeDateRangeShortcutItems` are `AnalyticsFiltersHeader`'s
   * OWN preset-naming logic, but they live in the shared `dateRangeChrome` module and are
   * called here directly — not copied. Two places computing what "This billing period"
   * MEANS is the drift §44 warns about; two places calling the same exported function are
   * not. `billingPeriodDateRange` / `BOOTSTRAP_DATE_RANGE` are the same story, for the
   * reset-on-delete default below.
   */
  const spend = useSelector(state => state.analytics.roi?.roi?.spend)
  const periodStart = spend?.current_period_start
  const periodEnd = spend?.current_period_end

  const shortcutItems = useMemo(
    () =>
      makeDateRangeShortcutItems(
        periodStart && periodEnd ? { billingPeriod: { start: periodStart, end: periodEnd } } : {},
      ),
    [periodStart, periodEnd],
  )

  const dateRangeValue: DateRange<Dayjs> = [dayjs(filters.dateRange.from), dayjs(filters.dateRange.to)]
  const presetLabel = useMemo(
    () => matchDateRangeShortcut(dateRangeValue, shortcutItems),
    // Redux VALUES, not `dateRangeValue` — see `AnalyticsFiltersHeader`'s identical note;
    // that array is rebuilt every render and would recompute the match every pass.
    [filters.dateRange.from, filters.dateRange.to, shortcutItems],
  )

  const dateChipText = useMemo(() => {
    const from = dayjs(filters.dateRange.from).format(DATE_RANGE_FIELD_FORMAT)
    const to = dayjs(filters.dateRange.to).format(DATE_RANGE_FIELD_FORMAT)
    // No time-of-day component here even when the header's field would show one (a
    // hand-picked or chart-promoted range) — a compact chip is the wrong place for the
    // ~44-character timed form; the field itself remains the exact, authoritative display.
    return presetLabel ? `${presetLabel} · ${from} – ${to}` : `${from} – ${to}`
  }, [filters.dateRange.from, filters.dateRange.to, presetLabel])

  /**
   * Reset target for the date chip's `X` — never `[]`/`null` (a range has no "unset"
   * state; see the file header's constraint 2). The SAME default the page bootstraps to:
   * the billing period once its boundaries are known, else the rolling 30-day window.
   */
  const handleResetDateRange = () => {
    const defaultRange =
      billingPeriodDateRange({ current_period_start: periodStart, current_period_end: periodEnd }) ??
      BOOTSTRAP_DATE_RANGE
    dispatch(setAnalyticsFilters({ dateRange: defaultRange }))
  }

  const described = useMemo<Partial<Record<AnalyticsFilterField, DescribedValues>>>(() => {
    const ownerNames = new Map(
      (owners?.items ?? []).map(owner => [owner.id, owner.name || owner.email] as const),
    )
    const productNames = new Map(
      (productsData?.data ?? []).map(product => [product.id, product.title] as const),
    )
    const certificateNames = new Map(
      (certificates?.data ?? []).map(
        certificate => [certificate.id, certificate.product?.title || certificate.id] as const,
      ),
    )
    const statusLabels = new Map(CERT_STATUS_OPTIONS.map(option => [option.value, option.label] as const))
    // `tampered` -> "Opened". The chip must read the same word the toolbar control, the
    // drawer field and the map's own pins use; the raw value is not that word.
    const scanStatusLabels = new Map(
      SCAN_STATUS_OPTIONS.map(option => [option.value, option.label] as const),
    )

    return {
      // Statuses are COLLAPSED first: the four in-flight transfer stages are stored
      // separately and all read "Transfer", so describing the raw values would render
      // `Certificate Status: Transfer, Transfer +2` for one selected chip.
      [FILTER_FIELDS.status]: describeValues(collapseStatuses(filters.certificateStatuses), v =>
        statusLabels.get(v),
      ),
      // No collapse step, unlike the certificate axis above: no two scan outcomes share a
      // label, so there is no many-to-one to undo before describing them.
      [FILTER_FIELDS.scanStatus]: describeValues(filters.scanStatuses, v => scanStatusLabels.get(v)),
      [FILTER_FIELDS.owner]: describeValues(filters.ownerIds, v => ownerNames.get(v)),
      [FILTER_FIELDS.product]: describeValues(filters.productIds, v => productNames.get(v)),
      [FILTER_FIELDS.certificate]: describeValues(filters.certificateIds, v => certificateNames.get(v)),
      // The location VALUE is the country name as stored on the scan row (see the
      // header's `locationOptions` note — sending the ISO-2 code matched nothing), so it
      // is already its own label and there is no list to miss.
      [FILTER_FIELDS.location]: describeValues(filters.location, v => v),
    }
  }, [
    filters.certificateStatuses,
    filters.scanStatuses,
    filters.ownerIds,
    filters.productIds,
    filters.certificateIds,
    filters.location,
    owners,
    productsData,
    certificates,
  ])

  const applied = FIELD_ORDER.map(field => ({ field, values: described[field] })).filter(
    (entry): entry is { field: AnalyticsFilterField; values: DescribedValues } =>
      !!entry.values && entry.values.total > 0,
  )

  /**
   * `null` is the whole test — see `AnalyticsFilters.area` in `store/analytics/types`: a
   * ring is only ever stored at 3+ vertices, so there is no degenerate non-null value to
   * guard against the way `total > 0` guards the array fields above.
   */
  const hasArea = filters.area !== null

  // Constraint 3 — the date chip is unconditional (see the file header), so this row is
  // never null any more; there is no longer an "empty" state to gate on.

  return (
    <Stack
      direction="row"
      flexWrap="wrap"
      useFlexGap
      alignItems="center"
      // 8px between chips, 8px between wrapped rows. Explicit `rowGap` because
      // `spacing` on a wrapping Stack sets the column gap only.
      spacing={2}
      rowGap={2}
      // Matches the 16px `ActionLine` already leaves above it, so the row sits in the
      // same rhythm as the toolbar rather than announcing itself as a new band.
      //
      // `toolbarContainerSx` declares the SAME query container the toolbar's own row
      // declares (VRG-517). The `*ChipInverseSx` helpers below are container queries, and a
      // container query with no matching ancestor never matches — which would leave every
      // fallback chip permanently visible, duplicating the toolbar control sitting an inch
      // above it at every width. This row and the toolbar are siblings of identical width,
      // so the same threshold resolves the same way in both, which is what guarantees
      // exactly one of each control/chip pair is ever on screen.
      sx={{ pb: 4, ...toolbarContainerSx }}
    >
      {/*
        ── THE DATE CHIP — ALWAYS FIRST, ALWAYS PRESENT ──────────────────────────────
        Not `clickable`: there is no drawer field this can reopen from this file without
        depending on `AnalyticsFiltersHeader`'s internal (unexported) drawer column key,
        which is also mid-edit for this same change. See the file header's constraint 2
        for why this chip exists unconditionally, and `handleResetDateRange` for why its
        `X` is safe despite the field never being "unset".
      */}
      <TagChip
        deleteIcon={<XIcon strokeWidth={1.5} />}
        onDelete={handleResetDateRange}
        aria-label={`Date range: ${dateChipText}`}
        label={<ChipLabel fieldLabel="Date range" valueText={dateChipText} />}
        // FALLBACK ONLY — hidden while the toolbar's own date control is on screen.
        // With both visible the chip repeated the control sitting an inch above it,
        // verbatim. Each chip mirrors ITS OWN control's threshold — the two drop in
        // stages, so the date chip appears later than the status one.
        sx={{ ...CHIP_SX, ...dateChipInverseSx('inline-flex') }}
      />

      {/*
        ⚠️ A WORKING "VIEW BY" MENU USED TO RENDER HERE AND IS DELETED (VRG-512).

        It was the one entry in this row that was a CONTROL rather than a statement of a
        value set elsewhere — a necessary anomaly, because its toolbar counterpart's stage
        (1904px) put it off screen at every width this page is used at, so a read-only chip
        would have left the feature visible and unreachable.

        The anomaly is gone with the page-level control it fronted. The bucket is a display
        property of ONE card, so it renders in that card's own header beside its line/bar
        toggle (`AnalyticsCardGranularityControl`) — on screen at every width, and never
        standing for a value that scopes the page. This row is back to what it says it is:
        the page's applied FILTERS.
      */}

      {applied.map(({ field, values }) => {
        const named = values.named.slice(0, MAX_NAMED_VALUES)
        const remaining = values.total - named.length
        // Nothing resolved (a selection whose entities are outside the loaded page) —
        // say how many rather than name none. See `DescribedValues`.
        const valueText = named.length
          ? `${named.join(', ')}${remaining > 0 ? ` +${remaining}` : ''}`
          : `${values.total} selected`

        const chip = (
          <TagChip
            key={field}
            clickable
            onClick={() => onOpenFilter(field)}
            // §38 — `X`, not a trash glyph. Removing a filter severs nothing and
            // destroys no record; it is set-removal, and it is undone by reapplying it.
            deleteIcon={<XIcon strokeWidth={1.5} />}
            onDelete={() => dispatch(setAnalyticsFilters({ [field]: [] }))}
            aria-label={`${FIELD_LABELS[field]}: ${valueText}`}
            label={<ChipLabel fieldLabel={FIELD_LABELS[field]} valueText={valueText} />}
            /*
              The two STATUS fields are the only ones in this list with a TOOLBAR control of
              their own, so they are the only ones that become duplicates at wide widths —
              each hides while ITS OWN control is visible and returns when that control
              drops. The two drop at DIFFERENT thresholds (staged; see
              `AnalyticsFiltersHeader`), so they must not share one inverse: at a width where
              scan status has dropped but certificate status has not, one chip is required
              and the other would be a duplicate of the control an inch above it.

              Owner / Product / Certificate / Location live solely in the drawer, so their
              chips are the only on-screen statement of their value and must always render.
            */
            sx={
              field === FILTER_FIELDS.status
                ? { ...CHIP_SX, ...statusChipInverseSx('inline-flex') }
                : field === FILTER_FIELDS.scanStatus
                  ? { ...CHIP_SX, ...scanStatusChipInverseSx('inline-flex') }
                  : CHIP_SX
            }
          />
        )

        // The full set on hover, but ONLY when the chip is actually hiding something —
        // a tooltip repeating a label already fully visible is the classic noise case.
        return remaining > 0 ? (
          <Tooltip key={field} title={values.named.join(', ')} placement="top" arrow>
            {chip}
          </Tooltip>
        ) : (
          chip
        )
      })}

      {/*
        ── THE `Location: drawn` CHIP (VRG-480 follow-up) ────────────────────────────
        Not built from `described`/`DescribedValues` — a drawn ring has no id list and no
        label lookup, just "is one set or not" (see `hasArea`).

        The count deliberately does NOT try to state how many scans/certificates the ring
        matched. The page can hold more than one map card at once (VRG-467's per-metric
        variants), each asking its own `mode`/`dataType` question, so there is no single
        number that is unambiguously "the map's count" to read back here — and guessing
        by matching an arbitrary cache entry would risk showing a count that belongs to a
        DIFFERENT card's question, which is a worse lie than showing none (see
        `describeValues`'s own honesty rule). What this chip must avoid is the KPI's
        number (`state.analytics.totals`), and the reason has CHANGED rather than gone
        away. It used to be that the KPI row did not see this predicate at all, its scan
        tile being counted from `certificate_action_logs`; since VRG-452 moved that tile
        onto the `scans` table the drawn area DOES narrow it — but only it. The
        certificate and transfer tiles in the same row still ignore the ring entirely,
        because a certificate or a transfer carries no coordinates. So "421" beside
        "Location: drawn" would still be a claim about the whole totals slot that only one
        of its three numbers supports. Never touching that slot is what keeps that from
        happening — see the map card itself (`ScansByLocation`'s own
        `{points.length} scans` caption, right beside the shape) for the actual count.
      */}
      {hasArea && (
        <TagChip
          clickable
          onClick={() => {
            // `focusScansMap` returns `false` when no map card is on the canvas — see its
            // own doc comment. There is nowhere else to send the reader in that case, so
            // this is a deliberate no-op rather than a fallback to `onOpenFilter` (`area`
            // has no drawer column to open).
            focusScansMap()
          }}
          // §38 — `X`, not a trash glyph; clearing an area severs nothing.
          deleteIcon={<XIcon strokeWidth={1.5} />}
          onDelete={() => dispatch(setAnalyticsFilters({ area: null }))}
          aria-label={`${FIELD_LABELS[FILTER_FIELDS.area]}: drawn`}
          label={<ChipLabel fieldLabel={FIELD_LABELS[FILTER_FIELDS.area]} valueText="drawn" />}
          sx={CHIP_SX}
        />
      )}
    </Stack>
  )
}
